import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Building2, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { submitInquiry } from '../services/api';
import { contactInfo } from '../mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    productInterest: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitInquiry(formData);
      
      toast.success("Inquiry Sent Successfully!", {
        description: "We will get back to you within 24 hours.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        productInterest: ''
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Failed to send inquiry", {
        description: "Please try again later or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 section-teal text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Get In Touch</h1>
          <div className="decorative-line max-w-md"></div>
          <p className="text-xl max-w-3xl mx-auto mt-6 text-gray-100">
            Have questions or ready to start your order? We're here to help
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle style={{ color: 'var(--primary-teal)' }}>
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--gold-accent)' }}
                    >
                      <Mail size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--text-dark)' }}>
                        Email Us
                      </h3>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-sm hover:underline block"
                        style={{ color: 'var(--primary-teal)' }}
                      >
                        {contactInfo.email}
                      </a>
                      <a 
                        href={`mailto:${contactInfo.emailSecondary}`}
                        className="text-sm hover:underline block"
                        style={{ color: 'var(--primary-teal)' }}
                      >
                        {contactInfo.emailSecondary}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--primary-teal)' }}
                    >
                      <Phone size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--text-dark)' }}>
                        Call Us
                      </h3>
                      <a 
                        href={`tel:${contactInfo.phone}`}
                        className="text-sm hover:underline"
                        style={{ color: 'var(--primary-teal)' }}
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--gold-accent)' }}
                    >
                      <MapPin size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--text-dark)' }}>
                        Visit Us
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                        {contactInfo.address}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg" style={{ backgroundColor: 'var(--primary-teal)' }}>
                <CardContent className="p-6 text-white">
                  <h3 className="text-xl font-bold mb-3">Business Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span>9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span>10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ color: 'var(--primary-teal)' }}>
                    Send Us An Inquiry
                  </CardTitle>
                  <p className="text-sm mt-2" style={{ color: 'var(--text-medium)' }}>
                    Fill out the form below and we'll respond within 24 hours
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                          Full Name *
                        </label>
                        <div className="relative">
                          <User 
                            className="absolute left-3 top-1/2 transform -translate-y-1/2" 
                            size={18}
                            style={{ color: 'var(--text-light)' }}
                          />
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail 
                            className="absolute left-3 top-1/2 transform -translate-y-1/2" 
                            size={18}
                            style={{ color: 'var(--text-light)' }}
                          />
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone 
                            className="absolute left-3 top-1/2 transform -translate-y-1/2" 
                            size={18}
                            style={{ color: 'var(--text-light)' }}
                          />
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+1 234 567 8900"
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                          Company Name
                        </label>
                        <div className="relative">
                          <Building2 
                            className="absolute left-3 top-1/2 transform -translate-y-1/2" 
                            size={18}
                            style={{ color: 'var(--text-light)' }}
                          />
                          <Input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your Company"
                            className="pl-10"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                        Product Interest
                      </label>
                      <select
                        name="productInterest"
                        value={formData.productInterest}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2"
                        style={{ 
                          color: 'var(--text-dark)'
                        }}
                      >
                        <option value="">Select a category</option>
                        <option value="blended-spices">Blended Spices</option>
                        <option value="whole-ground-spices">Whole & Ground Spices</option>
                        <option value="pulses-grains">Pulses & Grains</option>
                        <option value="tea-coffee">Tea & Coffee</option>
                        <option value="fmcg">FMCG Products</option>
                        <option value="household">Household Items</option>
                        <option value="flour">Flour & Grains</option>
                        <option value="ready-to-eat">Ready to Eat & Premix</option>
                        <option value="pooja">Pooja Articles</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                        Subject *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What is your inquiry about?"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>
                        Message *
                      </label>
                      <div className="relative">
                        <MessageSquare 
                          className="absolute left-3 top-3" 
                          size={18}
                          style={{ color: 'var(--text-light)' }}
                        />
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your requirements, quantities, delivery location, etc."
                          required
                          rows={6}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="btn-gold w-full text-lg py-6"
                    >
                      {isSubmitting ? (
                        <>Sending...</>
                      ) : (
                        <>
                          Send Inquiry
                          <Send className="ml-2" size={20} />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-0 section-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full h-96 rounded-lg shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.6896447073654!2d73.11089047497754!3d19.033097982165367!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7e9d5a6b6d6d5%3A0x9c8e1e6c4d4c4c4c!2sKhanda%20Colony%2C%20New%20Panvel%2C%20Panvel%2C%20Navi%20Mumbai%2C%20Maharashtra%20410206!5e0!3m2!1sen!2sin!4v1709561234567!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Triton Global Trade Location"
            ></iframe>
          </div>
          <div className="mt-4 text-center">
            <a 
              href="https://www.google.com/maps/search/C1303+Tulsi+Prerna+Sector+1+Khanda+Colony+New+Panvel+410206+Maharashtra+India"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[var(--primary-teal)] hover:underline"
            >
              <MapPin size={18} className="mr-2" />
              Get Directions on Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
